/**
 * 審核核發時「改了哪幾格」的比對（批121）。
 *
 * ## 為什麼只送有改的鍵，不整包送
 *
 * `POST /license-requests/{id}/approve` 的內容欄位全是選填，**沒帶＝照申請單原樣**
 * （後端 `as_content()` 只讀 `model_fields_set`）。整包送的話，審核者什麼都沒動也會變成
 * 一次「覆寫」——稽核的 `overrides` 會塞滿雜訊、政策也會拿「表單展開後的值」重算，
 * 把原本照模板的申請判成客製內容而升級審核身分。
 *
 * ## 判準是「深層值相等」
 *
 * 表單的 `quotas`／`modules`／`policy` 都是物件，每次 render 都是新參照，`!==` 一定為真。
 * 這裡用穩定排序後的 JSON 比對——鍵順序不影響結果，`null` 與缺鍵**不**視為相同
 * （那是配額語意的核心：`null`＝無上限、缺鍵＝依 `absent_default`）。
 *
 * ★ 期限是**兩個欄位一組**（`term_days` ＋ `term_minutes`，總期限＝天×1440＋分）：任一個變了
 *   就得兩個一起送，只送一半後端會拿另一半的舊值算出完全不同的期限。
 */

/** 內容欄位的中文名（給確認對話框列「你要改的是這幾項」）。 */
export const OVERRIDE_LABELS: Record<string, string> = {
  plan: '套裝',
  modules: '模組',
  quotas: '數量配額',
  term_days: '期限',
  term_minutes: '期限',
  policy: '行為參數',
  not_before: '啟用時間',
  expires_at: '到期時刻',
}

/** 期限的兩個欄位——動一個就要一起送。 */
const TERM_KEYS = ['term_days', 'term_minutes'] as const

/** 鍵排序後的 JSON（物件比對用；`undefined` 與缺鍵同義，`null` 是值）。 */
export function stableJson(v: unknown): string {
  if (v === undefined) return 'undefined'
  return JSON.stringify(v, (_k, val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.fromEntries(Object.entries(val as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
    }
    return val
  })
}

/**
 * `current` 相對於 `baseline` 真的變了的鍵（開窗當下快照 vs 現在的表單）。
 *
 * 只看兩邊都認得的鍵；期限那兩欄任一變動就一起回。
 */
export function changedContentKeys(
  baseline: Record<string, unknown>,
  current: Record<string, unknown>,
): string[] {
  const keys = new Set([...Object.keys(baseline), ...Object.keys(current)])
  const out: string[] = []
  for (const k of keys) {
    if (stableJson(baseline[k]) !== stableJson(current[k])) out.push(k)
  }
  if (TERM_KEYS.some((k) => out.includes(k))) {
    for (const k of TERM_KEYS) if (!out.includes(k)) out.push(k)
  }
  return out.sort()
}

/** 從整份表單內容裡挑出要送的那幾個鍵。 */
export function pickOverride(
  body: Record<string, unknown>,
  keys: string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const k of keys) if (k in body) out[k] = body[k]
  return out
}

/** 改動鍵 → 去重後的中文名清單（確認對話框用）。 */
export function overrideLabels(keys: string[]): string[] {
  const seen = new Set<string>()
  for (const k of keys) seen.add(OVERRIDE_LABELS[k] ?? k)
  return [...seen]
}
